import { useCallback, useEffect, useRef, useState } from 'react'
import { CustomCellEditorProps } from 'ag-grid-react'
import { ProjectMemberSearchOption } from '../../../../../domain/value-object/ProjectMemberSearchOption'
import { useProjectMemberRepository } from '../../../../../services/adaptors/projectMemberRepositoryAdaptor'
import { useDebounce } from '../../../../hooks/useDebounce'
import {
  ProjectMemberPopper,
  ProjectMemberPopperHandle,
} from '../../../../features/ProjectMemberPopper'
import { ProjectMemberInput } from '../../../../features/ProjectMemberInput'
import { moveToNextRow } from './index'

interface Props extends CustomCellEditorProps {}

export const ProjectMemberCellEditor = (props: Props) => {
  const { value, api, column, rowIndex } = props
  const [val, setVal] = useState<ProjectMemberSearchOption | null>(
    value || null
  )
  const [options, setOptions] = useState<ProjectMemberSearchOption[]>([])
  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebounce(searchText, 300)
  const { searchAsProjectMemberSearchOption } = useProjectMemberRepository()
  const inputContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popperRef = useRef<ProjectMemberPopperHandle>(null)
  const firstRendered = useRef(true)

  useEffect(() => {
    if (firstRendered.current) {
      firstRendered.current = false
      return
    }
    api.stopEditing()
    api.setFocusedCell(rowIndex, column)
  }, [val, api])

  useEffect(() => {
    let isMounted = true
    const fn = async () => {
      const response =
        await searchAsProjectMemberSearchOption(debouncedSearchText)
      if (isMounted) {
        setOptions(response)
      }
    }
    fn()
    return () => {
      isMounted = false
    }
  }, [searchAsProjectMemberSearchOption, debouncedSearchText])

  const handleEnter = useCallback(() => {
    if (api && rowIndex !== api.getLastDisplayedRowIndex()) {
      setTimeout(() => moveToNextRow(api, rowIndex, column), 0)
    }
  }, [api, rowIndex, column])

  const handleSelect = useCallback((member: ProjectMemberSearchOption) => {
    props.onValueChange(member)
    setVal(member)
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowDown()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowUp()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const selected = popperRef.current?.enter()
        if (selected) {
          handleEnter()
        }
      }
    },
    [handleEnter]
  )

  return (
    <>
      <ProjectMemberInput
        ref={inputContainerRef}
        inputRef={inputRef}
        value={searchText}
        onChange={setSearchText}
        onKeyDown={handleKeyDown}
      />
      <ProjectMemberPopper
        ref={popperRef}
        open={true}
        anchorEl={inputContainerRef.current}
        options={options}
        onSelect={handleSelect}
        inputRef={inputRef}
        searchText={searchText}
      />
    </>
  )
}
